import React, { useState, useEffect } from 'react';

const DISCORD_INVITE = 'https://discord.gg/vehU36G44N';

function LoginModal({ isOpen, onClose, onConfirm }) {
    // 'intro'  -> choose Login (existing/verified users) or Register (new users)
    // 'register' -> explains how to verify in Discord, then "Verified? Continue..."
    const [stage, setStage] = useState('intro');

    // Always start fresh on the intro stage each time the modal opens.
    useEffect(() => {
        if (isOpen) setStage('intro');
    }, [isOpen]);

    if (!isOpen) return null;

    const grayButton =
        "bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-colors";
    const maroonButton =
        "bg-marron hover:opacity-90 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity";
    const buttonShadow = { boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' };
    const maroonStyle = { ...buttonShadow, backgroundColor: '#5A2929' };

    // Shared footer for the register/help stages: Back (to the given stage), Cancel, Continue.
    const renderFooter = (backTo) => (
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
            <button
                onClick={() => setStage(backTo)}
                className="text-gray-600 hover:text-gray-800 font-['Jersey_10'] uppercase text-[15px] tracking-[1px] transition-colors order-3 sm:order-1"
            >
                ← Back
            </button>
            <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto order-1 sm:order-2">
                <button
                    onClick={onClose}
                    className={`${grayButton} w-full sm:w-auto`}
                    style={buttonShadow}
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className={`${maroonButton} w-full sm:w-auto`}
                    style={maroonStyle}
                >
                    Verified? Continue...
                </button>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={onClose}
            ></div>

            <div
                className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[101] max-h-[90vh] overflow-y-auto"
                style={{ boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)' }}
            >
                <div className="flex items-center gap-2 mb-4">
                    <img src={`${process.env.PUBLIC_URL}/img/passlock.gif`} alt="" />
                    <h2 className="text-maroon uppercase font-['Jersey_10'] text-[17px] tracking-[1px]">
                        {stage === 'intro'
                            ? 'Login or Register'
                            : stage === 'help'
                            ? 'Verification Help'
                            : 'Verify with OriginsBot'}
                    </h2>
                </div>

                <hr className="border-gold mb-4" />

                {stage === 'intro' ? (
                    <>
                        <a
                            href={DISCORD_INVITE}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block mb-4"
                        >
                            <img
                                src={`${process.env.PUBLIC_URL}/img/discordbanner-login.png`}
                                alt="Join the Frankly Origins Discord"
                                className="block w-[400px] h-[120px] border border-black"
                                width={400}
                                height={120}
                                style={{ imageRendering: 'pixelated' }}
                            />
                        </a>
                        <div className="mb-6 space-y-3">
                            <p className="text-gray-800 font-['Jersey_10'] text-xl">
                                FranklyOrigins uses <span className="text-maroon">OriginsBot</span> as
                                its authentication service - that's our Discord bot, who you'll see
                                in the server as <span className="text-maroon">Frank</span>.
                            </p>
                            <p className="text-gray-800 font-['Jersey_10'] text-xl">
                                <span className="text-maroon">Already verified?</span> Choose{' '}
                                <span className="uppercase">Login</span> below. By logging in you agree
                                for OriginsBot to share your Habbo name with FranklyOrigins.
                            </p>
                            <p className="text-gray-800 font-['Jersey_10'] text-xl">
                                <span className="text-maroon">New here?</span> Choose{' '}
                                <span className="uppercase">Register</span> and we'll show you how to
                                verify in our Discord.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
                            <button
                                onClick={onClose}
                                className={`${grayButton} w-full sm:w-auto order-3 sm:order-1`}
                                style={buttonShadow}
                            >
                                Cancel
                            </button>
                            <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto order-1 sm:order-2">
                                <button
                                    onClick={() => setStage('register')}
                                    className="bg-gold hover:opacity-90 text-gray-900 w-full sm:w-auto px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity"
                                    style={buttonShadow}
                                >
                                    Register
                                </button>
                                <button
                                    onClick={onConfirm}
                                    className={`${maroonButton} w-full sm:w-auto`}
                                    style={maroonStyle}
                                >
                                    Login
                                </button>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                            <a
                                href="/privacy-policy"
                                target="_blank"
                                rel="noreferrer"
                                className="text-gray-500 hover:text-maroon underline font-['Jersey_10'] text-base transition-colors"
                            >
                                Privacy Policy
                            </a>
                            <span className="text-gray-400 text-base">·</span>
                            <a
                                href="/terms-of-service"
                                target="_blank"
                                rel="noreferrer"
                                className="text-gray-500 hover:text-maroon underline font-['Jersey_10'] text-base transition-colors"
                            >
                                Terms of Service
                            </a>
                            <span className="text-gray-400 text-base">·</span>
                            <a
                                href="/community-guidelines"
                                target="_blank"
                                rel="noreferrer"
                                className="text-gray-500 hover:text-maroon underline font-['Jersey_10'] text-base transition-colors"
                            >
                                Community Guidelines
                            </a>
                        </div>
                    </>
                ) : stage === 'register' ? (
                    <>
                        <div className="mb-6 space-y-4">
                            <a
                                href={DISCORD_INVITE}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/img/discordbanner-login.png`}
                                    alt="Join the Frankly Origins Discord"
                                    className="block w-[400px] h-[120px] border border-black"
                                    width={400}
                                    height={120}
                                    style={{ imageRendering: 'pixelated' }}
                                />
                            </a>

                            <a
                                href={DISCORD_INVITE}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full text-center text-white hover:opacity-90 px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity"
                                style={{ ...buttonShadow, backgroundColor: '#5865F2' }}
                            >
                                Join our Discord!
                            </a>

                            <div className="space-y-2">
                                <p className="text-gray-800 font-['Jersey_10'] text-xl">
                                    To get verified, head into our Discord and use{' '}
                                    <span className="text-maroon">OriginsBot</span> - he goes by{' '}
                                    <span className="text-maroon">Frank</span> in the server:
                                </p>
                                <ol className="list-decimal list-inside space-y-1 text-gray-800 font-['Jersey_10'] text-xl">
                                    <li>Head to our Discord server and go to the "Verify" channel.</li>
                                    <li>Follow <span className="text-maroon">Frank's</span> (OriginsBot's) prompts to link your Habbo name.</li>
                                </ol>

                                {/* Temporarily disabled
                                <button
                                    onClick={() => setStage('help')}
                                    className="block w-full text-center text-gray-700 hover:text-maroon hover:border-maroon border-2 border-gray-400 bg-white/30 px-4 py-3 font-['Jersey_10'] uppercase text-[15px] tracking-[1px] transition-colors"
                                >
                                    Having trouble verifying?
                                </button>
                                */}
                            </div>
                        </div>

                        {renderFooter('intro')}
                    </>
                ) : (
                    <>
                        <div className="mb-6 space-y-4">
                            <p className="text-gray-800 font-['Jersey_10'] text-xl">
                                Step-by-step guide to verifying with{' '}
                                <span className="text-maroon">Frank (OriginsBot)</span> in our Discord:
                            </p>

                            {/* TODO: add bot verification screenshots / detailed steps here.
                                Each step can be an image + caption block like the one below. */}
                            <div className="border-2 border-dashed border-gray-400 bg-white/30 text-center py-10 px-4">
                                <span className="text-gray-500 font-['Jersey_10'] text-base">
                                    Step-by-step screenshots coming soon
                                </span>
                            </div>
                        </div>

                        {renderFooter('register')}
                    </>
                )}
            </div>
        </div>
    );
}

export default LoginModal;
